import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PawPrint, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Paw-Verified trust signal.
 * - Shows live count.
 * - Toggle on/off per (user, trail).
 * - Anonymous users see the count only; CTA prompts sign-in.
 */
export default function PawVerifyButton({ trailId }) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ["paw-verify", trailId, user?.user_id || "anon"],
    queryFn: async () => {
      if (user) return (await api.get(`/trails/${trailId}/paw-verify/me`)).data;
      return (await api.get(`/trails/${trailId}/paw-verify`)).data;
    },
  });

  const mut = useMutation({
    mutationFn: async (verify) => {
      if (verify) return (await api.post(`/trails/${trailId}/paw-verify`)).data;
      return (await api.delete(`/trails/${trailId}/paw-verify`)).data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["paw-verify", trailId, user?.user_id || "anon"], data);
      toast.success(data.verified_by_me ? "Paw-Verified — thanks!" : "Verification removed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't update"),
  });

  const count = status.data?.count ?? 0;
  const mine = status.data?.verified_by_me ?? false;

  const onClick = () => {
    if (!user) {
      toast.message("Sign in to Paw-Verify this trail");
      return;
    }
    mut.mutate(!mine);
  };

  return (
    <button
      data-testid="paw-verify-btn"
      type="button"
      onClick={onClick}
      disabled={mut.isPending}
      className={
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors " +
        (mine
          ? "bg-brand-primary text-brand-sand border-brand-primary"
          : "bg-brand-surface text-brand-text border-brand-border hover:border-brand-primary/40") +
        " disabled:opacity-60"
      }
    >
      {mut.isPending ? <Loader2 size={12} className="animate-spin" /> : <PawPrint size={12} className={mine ? "fill-brand-sand" : ""} />}
      <span data-testid="paw-verify-count">{count}</span>
      <span className="hidden sm:inline">Paw-Verified</span>
    </button>
  );
}
